<template>
  <div v-html="year.turn_in_release_text"></div>

  <v-card>
    <v-card-title class="d-flex justify-space-between align-center"> Talent Releases</v-card-title>
    <v-card-text>
      <div v-if="talentReleases.length == 0" class="text-center text-body-2 font-italic mb-4">
        Please add Actor/Actress in the Cast and Crew section to see Talent Releases here.
      </div>
      <TalentReleaseComponent
        v-else
        v-for="talentRelease of talentReleases"
        :castCrew="talentRelease"
        :submission="submission"
        :releaseType="talentType" />
    </v-card-text>
  </v-card>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      Location Release
      <div class="d-flex justify-end mb-2">
        <v-icon color="primary" @click="addRelease('location')">mdi-plus</v-icon>
      </div>
    </v-card-title>
    <v-card-text>
      <div v-if="locationReleases.length == 0" class="text-center text-body-2 font-italic mb-4">
        Please click + icon to add a Location Release. This is needed if you filmed on private
        property or any location that requires a release.
      </div>
      <ReleaseComponent
        v-else
        v-for="locationRelease of locationReleases"
        :release="locationRelease"
        :submission="submission"
        :releaseType="locationType" />
    </v-card-text>
  </v-card>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      Music Releases
      <div class="d-flex justify-end mb-2">
        <v-icon color="primary" @click="addRelease('music')">mdi-plus</v-icon>
      </div>
    </v-card-title>
    <v-card-text>
      <div v-if="musicReleases.length == 0" class="text-center text-body-2 font-italic mb-4">
        Please click + icon to add a Music Release. This is needed if you used any copyrighted music
        in your film.
      </div>
      <ReleaseComponent
        v-else
        v-for="musicRelease of musicReleases"
        :release="musicRelease"
        :submission="submission"
        :releaseType="musicType" />
    </v-card-text>
  </v-card>
</template>
<script setup lang="ts">
import ReleaseComponent from '@/components/release/ReleaseComponent.vue';
import TalentReleaseComponent from '@/components/release/TalentReleaseComponent.vue';
import {
  ITeamCriteriaDTO,
  IYearDTO,
  ITeamDTO,
  ReleaseDTO,
  ReleaseTypeEnum,
  ReleaseTypeDTO,
  IReleaseDTO,
  ISubmissionCastCrewDTO,
  SubmissionCastCrewDTO,
} from '@/dtos';
import { useReleaseStore } from '@/stores';
import { useCastCrewStore } from '@/stores/modules/castcrew';
import { computed, ref, watch } from 'vue';

interface Props {
  registration: ITeamCriteriaDTO;
  year: IYearDTO;
  team: ITeamDTO;
}
const emit = defineEmits(['releaseStatus']);
const props = defineProps<Props>();
const year = computed(() => props.year);
const registration = computed(() => props.registration);
const releaseStore = useReleaseStore();
const castCrewStore = useCastCrewStore();
const submission = computed(() => registration.value.submissions[0]);
const submissionId = computed(() => submission.value.id);
const { data: castCrewData } = castCrewStore.getCastAndCrew(submissionId);

const { data: releaseTypesData } = releaseStore.getReleaseTypes();
const { data: releases } = releaseStore.getReleasesForSubmission(
  registration.value.submissions[0].id,
);

const locationType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Location.id) ||
    new ReleaseTypeDTO()
  );
});
const musicType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Music.id) ||
    new ReleaseTypeDTO()
  );
});
const talentType = computed(() => {
  return (
    releaseTypesData.value?.data.find((type) => type.id == ReleaseTypeEnum.Talent.id) ||
    new ReleaseTypeDTO()
  );
});

const addRelease = (type: 'location' | 'music' | 'talent') => {
  if (type === 'location') {
    locationReleases.value.push(new ReleaseDTO());
  } else if (type === 'music') {
    musicReleases.value.push(new ReleaseDTO());
  } else if (type === 'talent') {
    talentReleases.value.push(new SubmissionCastCrewDTO());
  }
};

const locationReleases = ref<IReleaseDTO[]>([]);
const musicReleases = ref<IReleaseDTO[]>([]);
const talentReleases = ref<ISubmissionCastCrewDTO[]>([]);

watch(
  () => [releases.value, castCrewData.value],
  () => {
    locationReleases.value =
      releases.value?.data.filter(
        (release) => release.releaseType?.id == ReleaseTypeEnum.Location.id,
      ) || [];
    musicReleases.value =
      releases.value?.data.filter(
        (release) => release.releaseType?.id == ReleaseTypeEnum.Music.id,
      ) || [];

    talentReleases.value =
      castCrewData.value?.data.filter((cast) => cast.castCrew?.name == 'Actor/Actress') || [];
  },
);

watch(
  () => [locationReleases.value, musicReleases.value, talentReleases.value],
  () => {
    let completed = true;

    for (const release of locationReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    for (const release of musicReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    for (const release of talentReleases.value) {
      if (!release.release) {
        completed = false;
        break;
      }
    }
    if (completed) {
      releaseStore.markDone.mutateAsync(submissionId.value);
    } else {
      releaseStore.markNotDone.mutateAsync(submissionId.value);
    }
    emit('releaseStatus', completed);
  },
  { deep: true },
);
</script>
